import React, { useEffect, useRef, useState } from "react";

type ProductRow = { [k: string]: any };

type Props = {
  show: boolean;
  onClose: () => void;
  onSubmit: () => void;
  formData?: any;
  productRows?: ProductRow[];
  currencyName?: string;
  headerLogoUrl?: string;
  footerImageUrl?: string;
  quotationFormat?: string;
};

export default function QuotationPreview({
  show,
  onClose,
  onSubmit,
  formData,
  productRows = [],
  currencyName = "INR",
  headerLogoUrl,
  footerImageUrl,
  quotationFormat,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [downloading, setDownloading] = useState(false);

  const handleSubmit = async () => {
    try {
      setDownloading(true);
      await onSubmit();
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (show && containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [show]);

  if (!show) return null;

  const totals = productRows.reduce(
    (a, r) => {
      const net = Number(r?.net_price || 0);
      const igstPct = Number(r?.igst || 0);
      const halfPct = igstPct / 2;

      const igstAmt = (net * igstPct) / 100;
      const halfAmt = (net * halfPct) / 100;

      a.net += net;
      a.igst += igstAmt;
      a.sgst += halfAmt;
      a.cgst += halfAmt;

      return a;
    },
    {
      net: 0,
      igst: 0,
      sgst: 0,
      cgst: 0,
    }
  );
  const state = formData?.state ?? formData?.company_details?.state?.name;
  const quotationType = formData?.quotation_type;

  const isMH = state === "Maharashtra";

  const taxTotal = isMH ? totals.sgst + totals.cgst : totals.igst;
  const hasText = (v?: string) =>
    !!v && v?.replace(/<[^>]*>|&nbsp;/g, "").trim().length > 0;

  const getColSpan = () => {
    if (quotationType === "GW") return 15;
    if (state === "Maharashtra") return 13;
    return 11;
  };

  const grandTotal = totals.net + taxTotal;
  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={toolbar}>
          <h4>QUOTATION PREVIEW</h4>
        </div>
        <div style={scrollWrap}>
          <div ref={containerRef} style={paper}>
            <style>{printCss}</style>

            {/* HEADER */}
            <table className="hdr">
              <tbody>
                <tr>
                  <td
                    style={{
                      width: "20%",
                      borderBottom: "none",
                      textAlign: "center",
                      border: "none",
                    }}
                  >
                    <div
                      className="logo"
                      style={{
                        display: "inline-block",
                        position: "relative",
                        textAlign: "center",
                        maxWidth: "65%",
                        marginTop: "-8px",
                      }}
                    >
                      {headerLogoUrl ? (
                        <img
                          src={headerLogoUrl}
                          alt="logo"
                          style={{ display: "block", margin: "0 auto" }}
                        />
                      ) : (
                        <div className="logo-fallback">QUOTATION</div>
                      )}

                      <p
                        style={{
                          margin: "-5px 0 0 0",
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#333",
                          textAlign: "center",
                        }}
                      >
                        ISO 9001-2015
                      </p>
                    </div>
                  </td>

                  <td
                    className="d-flex meta-cell"
                    style={{
                      border: "none",
                      lineHeight: "2",
                    }}
                  >
                    <div
                      className="meta"
                      style={{
                        width: "100%",
                        fontSize: "12px",
                        lineHeight: "1.6",
                      }}
                    >
                      {/* ROW 1 */}
                      <div style={{ width: "100%" }}>
                        <span
                          style={{
                            display: "inline-block",
                            minWidth: "60px",
                            fontWeight: 700,
                            fontSize: "12px",
                            verticalAlign: "top",
                            color: "#004f6e",
                          }}
                        >
                          Address :
                        </span>
                        <span
                          style={{ verticalAlign: "top", fontSize: "12px" }}
                        >
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;
                          <b>
                            217, 2nd Floor, Champaklal Industrial Estate, Sion
                            East, Mumbai – 400022, India
                            &nbsp;&nbsp;&nbsp;&nbsp;
                          </b>
                        </span>

                        <span
                          style={{
                            display: "inline-block",
                            minWidth: "55px",
                            fontWeight: 700,
                            marginLeft: "20px",
                            verticalAlign: "top",
                            color: "#004f6e",
                          }}
                        >
                          Call :
                        </span>
                        <span style={{ verticalAlign: "top" }}>
                          <b>91-022-43159100</b>
                        </span>
                      </div>

                      {/* ROW 2 */}
                      <div style={{ width: "100%" }}>
                        <span
                          style={{
                            display: "inline-block",
                            minWidth: "90px",
                            fontWeight: 700,
                            verticalAlign: "top",
                            color: "#004f6e",
                          }}
                        >
                          Email :
                        </span>
                        <span style={{ verticalAlign: "top" }}>
                          <b>
                            sales@chromatographyworld.com,&nbsp;&nbsp;&nbsp;&nbsp;
                            speed@chromatographyworld.com,&nbsp;&nbsp;&nbsp;&nbsp;
                            gm-support@chromatographyworld.com&nbsp;&nbsp;&nbsp;
                          </b>
                        </span>
                      </div>

                      {/* ROW 3 */}
                      <div style={{ width: "100%" }}>
                        <span
                          style={{
                            display: "inline-block",
                            minWidth: "90px",
                            fontWeight: 700,
                            color: "maroon",
                            verticalAlign: "top",
                          }}
                        >
                          GSTN :
                        </span>
                        <span style={{ verticalAlign: "top" }}>
                          <b>27AAGFC1217K1ZM</b>
                        </span>

                        <span
                          style={{
                            display: "inline-block",
                            minWidth: "130px",
                            fontWeight: 700,
                            marginLeft: "20px",
                            verticalAlign: "top",
                            color: "maroon",
                          }}
                        >
                          UDYAM/MSME No. :
                        </span>
                        <span style={{ verticalAlign: "top" }}>
                          <b>UDYAM-MH-19-0078510</b> &nbsp;&nbsp; &nbsp;&nbsp;
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </span>

                        <span
                          style={{
                            display: "inline-block",
                            minWidth: "50px",
                            fontWeight: 700,
                            marginLeft: "20px",
                            verticalAlign: "top",
                            color: "#004f6e",
                          }}
                        >
                          Web :
                        </span>
                        <span style={{ color: "blue", verticalAlign: "top" }}>
                          <b>www.chromatographyworld.com</b>
                        </span>
                      </div>

                      {/* ROW 4 */}
                      <div style={{ width: "100%" }}>
                        <span
                          style={{
                            display: "inline-block",
                            minWidth: "60px",
                            fontWeight: 700,
                            verticalAlign: "top",
                            color: "#004f6e",
                          }}
                        >
                          Bank :
                        </span>
                        <span style={{ verticalAlign: "top" }}>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;
                          <b>Kotak Mahindra Bank</b>
                        </span>

                        <span
                          style={{
                            display: "inline-block",
                            minWidth: "70px",
                            fontWeight: 700,
                            marginLeft: "20px",
                            verticalAlign: "top",
                            color: "#004f6e",
                          }}
                        >
                          Branch :
                        </span>
                        <span style={{ verticalAlign: "top" }}>Matunga</span>

                        <span
                          style={{
                            display: "inline-block",
                            minWidth: "50px",
                            fontWeight: 700,
                            marginLeft: "20px",
                            verticalAlign: "top",
                            color: "#004f6e",
                          }}
                        >
                          IFSC :
                        </span>
                        <span style={{ verticalAlign: "top" }}>
                          <b>KKBK0000644</b>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </span>

                        <span
                          style={{
                            display: "inline-block",
                            minWidth: "40px",
                            fontWeight: 700,
                            marginLeft: "20px",
                            verticalAlign: "top",
                            color: "#004f6e",
                          }}
                        >
                          A/C :
                        </span>
                        <span style={{ verticalAlign: "top" }}>
                          <b>4611234274</b>
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <table>
              <tr>
                <td
                  style={{
                    textAlign: "center",
                    padding: "5px 8px",
                    fontSize: "13px",
                    backgroundColor: "#2b2b2b",
                    color: "#f5f2e8",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    fontWeight: 700,
                  }}
                >
                  <b>Authorised For :</b> Qualisil, Qaliseal, Gas World,
                  Macherey Nagel, G.L.Science (GC Columns), S.A.S.Corporation,
                  Nomura Chemicals (Develosil), Sielc (Primesep), Sciencix, Poly
                  LC, MZAnalysentechnik, Sepax, Frontier Lab. Click&nbsp;
                  <b style={{ color: "#edd38c", textDecoration: "underline" }}>
                    www.chromatographyworld.com
                  </b>{" "}
                  for more details.
                </td>
              </tr>
            </table>

            <h6
              style={{
                margin: "5px",
                color: "#800000",
                textAlign: "center",
                fontSize: "18px",
              }}
            >
              Quotation
            </h6>

            {/* SHIPPING */}
            <table className="ship">
              <tbody>
                {/* Row 1 */}
                <tr>
                  <th>Company</th>
                  <td colSpan={6}>
                    {formData?.company_name ??
                      formData?.company_details?.company_name}
                  </td>
                  <th>GSTN</th>
                  <td colSpan={3}>
                    {formData?.gst_number ??
                      formData?.company_details?.gst_number}
                  </td>
                </tr>

                {/* Row 2 : Address takes full width */}
                <tr>
                  <th>Address</th>
                  <td colSpan={2}>{formData?.billing_address}</td>
                  <th>Pin Code</th>
                  <td>{formData?.billing_pin_code}</td>
                  <th>City</th>
                  <td>{formData?.billing_city}</td>
                  <th>State</th>
                  <td>{state}</td>
                  <th>Country</th>
                  <td>
                    {formData?.country ??
                      formData?.company_details?.country?.name}
                  </td>
                </tr>

                {/* Row 3 */}
                <tr>
                  <th>Contact Person</th>
                  <td colSpan={1}>{formData?.contact_person}</td>

                  <th>Email</th>
                  <td colSpan={2}>{formData?.billing_email}</td>

                  <th>Landline (STD)</th>
                  <td colSpan={2}>{formData?.billing_landline}</td>

                  <th>Mobile</th>
                  <td colSpan={2}>{formData?.billing_mobile}</td>
                </tr>

                {/* Row 4 */}
                <tr>
                  <th>Quotation No.</th>
                  <td>{formData?.unique_quotation_no ?? "#"}</td>

                  <th>Quotation Date</th>
                  <td>{formData?.date}</td>

                  <th>Enq. Ref.</th>
                  <td colSpan={6}>
                    <b>{formData?.enq_ref ?? ""}</b>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ITEMS */}
            <table className="items">
              <thead>
                <tr>
                  <th className="money">Sr</th>
                  <th className="money">Part No.</th>
                  <th className="money">Description</th>

                  {quotationType == "GW" && (
                    <>
                      <th className="money">Maker</th>
                      <th className="money">UOM</th>
                    </>
                  )}
                  <th className="money">HSN</th>
                  <th className="money">Qty</th>
                  <th className="money-real">Unit Price ({currencyName})</th>
                  <th className="money-real">Disc %</th>
                  <th className="money-real">Net Price ({currencyName})</th>

                  {isMH ? (
                    <>
                      <th className="money-real">SGST %</th>
                      <th className="money-real">SGST AMT ({currencyName})</th>
                      <th className="money-real">CGST %</th>
                      <th className="money-real">CGST AMT ({currencyName})</th>
                    </>
                  ) : (
                    <>
                      <th className="money-real">IGST %</th>
                      <th className="money-real">IGST AMT ({currencyName})</th>
                    </>
                  )}

                  <th className="money-real">Total AMT ({currencyName})</th>
                  <th>Delivery</th>
                </tr>
              </thead>

              <tbody>
                {productRows.map((it, i) => {
                  const net = Number(it?.net_price || 0);
                  const igst = Number(it?.igst || 0);
                  const half = igst / 2;

                  const igstAmt = (net * igst) / 100;
                  const halfAmt = (net * half) / 100;

                  const rowTotal = isMH
                    ? net + halfAmt + halfAmt
                    : net + igstAmt;

                  const showDetails =
                    quotationType === "GW"
                      ? hasText(it.specification)
                      : hasText(it.product_specification);

                  return (
                    <React.Fragment key={i}>
                      {/* ================= MAIN PRODUCT ROW ================= */}
                      <tr
                        className={`item-row ${i % 2 === 0 ? "odd" : "even"}`}
                        style={{
                          backgroundColor:
                            quotationType === "GW"
                              ? i % 2 === 0
                                ? "#C1D9BF"
                                : "#D9E9D6"
                              : i % 2 === 0
                              ? "#faeff0"
                              : "#f1d3d6",
                        }}
                      >
                        <td>{i + 1}</td>
                        <td>{it.part_no}</td>
                        <td>
                          <b>{it.description}</b>
                        </td>

                        {quotationType === "GW" && (
                          <>
                            <td>
                              <b>{it?.principal?.type ?? it?.principal}</b>
                            </td>
                            <td>{it?.uom?.uom ?? it?.uom}</td>
                          </>
                        )}

                        <td>{it.hsn_code}</td>
                        <td>{it.quantity}</td>
                        <td className="money-real">{it.price}</td>
                        <td className="money-real">{it.discount}</td>
                        <td className="money-real">{net.toFixed(2)}</td>

                        {isMH ? (
                          <>
                            <td className="money-real">{half.toFixed(2)}</td>
                            <td className="money-real">{halfAmt.toFixed(2)}</td>
                            <td className="money-real">{half.toFixed(2)}</td>
                            <td className="money-real">{halfAmt.toFixed(2)}</td>
                          </>
                        ) : (
                          <>
                            <td>{igst.toFixed(2)}</td>
                            <td className="money-real">{igstAmt.toFixed(2)}</td>
                          </>
                        )}

                        <td className="money-real">{rowTotal.toFixed(2)}</td>
                        <td>
                          <span
                            dangerouslySetInnerHTML={{ __html: it.notes }}
                          />
                        </td>
                      </tr>

                      {/* ================= ALL DETAILS IN ONE TD ================= */}
                      {showDetails && (
                        <tr
                          className={`detail-row ${
                            i % 2 === 0 ? "odd" : "even"
                          }`}
                          style={{
                            backgroundColor:
                              i % 2 === 0 ? "#faeff0" : "#f1d3d6",
                          }}
                        >
                          <td
                            style={{
                              backgroundColor:
                                quotationType === "GW" ? "#f1d3d6" : undefined,
                            }}
                          ></td>
                          <td
                            colSpan={getColSpan()}
                            style={{
                              backgroundColor:
                                quotationType === "GW" ? "#f1d3d6" : undefined,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {hasText(it.product_specification) && (
                                <div className="detail-text  html-content">
                                  <b className="label-heading">Comments :</b>{" "}
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: it.product_specification,
                                    }}
                                  />
                                </div>
                              )}

                              {quotationType === "GW" &&
                                hasText(it.specification) && (
                                  <div className="detail-text html-content">
                                    <b className="label-heading">
                                      Specification with Heading :
                                    </b>{" "}
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: it.specification,
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* ================= NOTES ================= */}
                {hasText(formData?.product_description) && (
                  <tr style={{ backgroundColor: "#bcecfa" }}>
                    <td
                      colSpan={
                        quotationType === "GW"
                          ? 16
                          : state === "Maharashtra"
                          ? 14
                          : 13
                      }
                      style={{ border: "none", padding: "6px 4px" }}
                    >
                      <b>NOTES :</b>{" "}
                      <span
                        dangerouslySetInnerHTML={{
                          __html: formData.product_description,
                        }}
                      />
                    </td>
                  </tr>
                )}

                {state === "Maharashtra" && (
                  <tr className="total-row">
                    <td
                      colSpan={quotationType === "GW" ? 9 : 7}
                      className="label"
                    >
                      Grand Total ({currencyName})
                    </td>

                    <td className="money-real">{totals.net.toFixed(2)}</td>
                    <td></td>
                    <td className="money-real">{totals.sgst.toFixed(2)}</td>
                    <td></td>
                    <td className="money-real">{totals.cgst.toFixed(2)}</td>

                    <td className="money-real">{grandTotal.toFixed(2)}</td>

                    <td></td>
                  </tr>
                )}

                {state !== "Maharashtra" && (
                  <tr className="total-row">
                    <td
                      colSpan={quotationType === "GW" ? 9 : 7}
                      className="label"
                    >
                      Grand Total ({currencyName})
                    </td>

                    <td className="money-real">{totals.net.toFixed(2)}</td>
                    <td></td>
                    <td className="money-real">{totals.igst.toFixed(2)}</td>

                    <td className="money-real">{grandTotal.toFixed(2)}</td>

                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* ================= FOOTER TOTALS ================= */}
            <table className="footer">
              <tbody>
                <tr>
                  <td
                    colSpan={3}
                    style={{
                      backgroundImage: `url(${footerImageUrl})`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                      backgroundSize: "100% 100%",
                      padding: 0,
                      verticalAlign: "top",
                      border: "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "5px",
                      }}
                    >
                      {/* TERMS */}

                      {formData?.show_note && (
                        <div style={{ width: "55%" }}>
                          <div
                            style={{
                              color: "#004f6e",
                              fontWeight: 700,
                              fontSize: "13px",
                              marginBottom: "5px",
                            }}
                          >
                            Terms & Conditions
                          </div>

                          <div
                            className="html-content"
                            dangerouslySetInnerHTML={{
                              __html: formData?.payment_term_condition || "",
                            }}
                          />
                        </div>
                      )}

                      {/* SIGNATURE */}
                      <div
                        style={{
                          marginTop: "10px",
                          width: "25%",
                          color: "#333",
                          fontWeight: 700,
                          marginLeft: "auto",
                        }}
                      >
                        <p
                          style={{
                            color: "#004f6e",
                            margin: "0 0 2px 0",
                            fontSize: "12px",
                            textAlign: "right",
                          }}
                        >
                          For Chromatography World
                        </p>
                        <p
                          style={{
                            color: "#004f6e",
                            margin: 10,
                            fontSize: "12px",
                            textAlign: "right",
                          }}
                        >
                          Authorized Signatory
                        </p>
                        <p
                          style={{
                            color: "#333",
                            margin: 2,
                            fontSize: "12px",
                            textAlign: "right",
                          }}
                        >
                          <u>{formData?.prepard_by}</u>
                        </p>
                        <br />
                      </div>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td
                    colSpan={100}
                    style={{
                      backgroundColor: "#c9f1ff",
                      color: "#0b3a4a",
                      textAlign: "center",
                      padding: "10px 12px",
                      border: "none",
                      letterSpacing: "0.2px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        marginBottom: "10px",
                      }}
                    >
                      {quotationFormat}
                    </div>

                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "maroon",
                      }}
                    >
                      We look forward to your valuable order. Thank you!
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div style={toolbar}>
          <h5> </h5>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-md btn-soft-warning" onClick={onClose}>
              Edit
            </button>
            <button
              className="btn btn-md btn-soft-success"
              onClick={handleSubmit}
              disabled={downloading}
            >
              {downloading
                ? "Generating…"
                : formData?.unique_quotation_no
                ? "Update Quotation"
                : "Generate Quotation"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- styles ---------------- */

const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.65)",
  zIndex: 9999,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const modal: React.CSSProperties = {
  width: "96%",
  maxWidth: 1400,
  height: "90vh",
  background: "white",
  borderRadius: 8,
  display: "flex",
  flexDirection: "column",
};

const toolbar: React.CSSProperties = {
  padding: 10,
  borderBottom: "1px solid #eee",
  display: "flex",
  justifyContent: "center",
  textAlign: "center",
};

const scrollWrap: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
};

const paper: React.CSSProperties = {
  background: "#fff",
  padding: 8,
  margin: "-10px auto",
  width: "auto", //
};

const printCss = `

/* ================= FONT LOAD : CALIBRI ================= */

@font-face {
  font-family: "Calibri";
  src: url("/fonts/calibri.ttf") format("truetype");
  font-weight: 400;
  font-style: normal;
}   

@font-face {
  font-family: "Calibri";
  src: url("/fonts/calibrib.ttf") format("truetype");
  font-weight: 700;
  font-style: normal;
}

@font-face {
  font-family: "Calibri";
  src: url("/fonts/calibrii.ttf") format("truetype");
  font-weight: 400;
  font-style: italic;
}

@font-face {
  font-family: "Calibri";
  src: url("/fonts/calibriz.ttf") format("truetype");
  font-weight: 700;
  font-style: italic;
}

/* ================= BASE ================= */
body, table, th, td {
  font-family: "Calibri", Candara, "Segoe UI", Arial, sans-serif;
  font-size: 12px;
  line-height: 1.4;
  font-weight: 400;
  color: #1f1f1f;
  font-variant-numeric: tabular-nums;
}


table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

/* ================= UNIFORM BORDER SYSTEM ================= */
th, td {
  padding: 4px;
  vertical-align: top;
  word-break: break-word;
  overflow-wrap: anywhere;
  border: 0.4px solid #C2C9CC;
}

table tr th:first-child,
table tr td:first-child {
  border-left: 0.4px solid #C2C9CC;
}

table thead tr:first-child th,
table tbody tr:first-child td {
  border-top: 0.4px solid #C2C9CC;
}

tr {
  page-break-inside: auto;
}

/* ================= HEADINGS (ONE SIZE) ================= */
.hdr th,
.ship th,
.items th {
  font-size: 12px;              /* HEADING SIZE */
  font-weight: 700;
}

/* ================= HEADER TABLE ================= */
.hdr {
  background: #bcecfa;
}

.hdr th {
  color: #004f6e;
}

.hdr td {
  color: #333;
}

/* ================= SHIP TABLE ================= */
.ship {
  background: #f2f8f9;
  margin-bottom: 7px;
  word-break: normal;
}

.ship th {
  color: #004f6e;
     white-space: nowrap;
}

.ship td {
  color: #222;
}

/* ================= ITEMS ================= */
.items {
  margin-bottom: 7px;
}

.items th {
  background: #eef6f9;
  color: #004f6e;
  text-align: left;
}

.items td {
  color: #333;
}

/* ================= MONEY — NO WRAP ================= */
.items th.money,
.items td.money,
.items tbody tr.total-row td {
  white-space: nowrap !important;
  word-break: keep-all !important;
  overflow-wrap: normal !important;
}

.items th.money-real,
  .items td.money-real {
    white-space: nowrap;
    text-align: right;
    padding: 2px;
  }

  .total-row th.money-real,
  .items td.money-real {
    white-space: nowrap;
    text-align: right;
    padding: 2px;
  }

/* ================= SPEC / COMMENT ================= */
.spec-row td,
.comment-row td {
  font-size: 10px;              /* ONE SIZE SMALLER THAN CONTENT */
  line-height: 1.15;
  padding-top: 2px;
  padding-bottom: 2px;
}

/* ================= TOTAL ROW ================= */
.items tbody tr.total-row td {
  background: #333333 !important;
  color: #f5f2e8;
  font-size: 14px;              /* SAME AS HEADINGS */
  font-weight: 700;
  border: none !important;
}

.items tbody tr.total-row .label {
  text-align: center;
}

/* ================= META ================= */
.meta-label {
  color: #004f6e;
  text-transform: capitalize;
  letter-spacing: 1.5px;
}

.html-content ol li[data-list="ordered"] {
  list-style-type: decimal !important;
  list-style-position: outside !important;
  padding-left: 0px !important;  
  margin: 0;
  font-weight: 700;
  margin-left:15px;
}

.html-content ol li[data-list="bullet"] {
  list-style-type: disc !important;
  list-style-position: outside !important;
  padding-left: 0px !important;  
  margin: 0;
  font-weight: 700;
  margin-left:5px;
}
.html-content ol li {
  list-style-type: decimal !important;
  list-style-position: outside !important;
  padding-left: 0px !important;  
  margin: 0;
  font-weight: 700;
  margin-left:0px;
}


.html-content li,p {
  margin-bottom: 2px;
}

.label-heading {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  color: #006c95;
  margin-bottom: 2px;
}

.detail-text {
  font-size: 12px;
  padding: 0;
  white-space: normal;
}

.ql-container,
.ql-editor {
  background: #ffffff !important;
  color: #000;
}
`;
